import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowDownRight, ArrowUpRight, Lock, Plus, Trash2, Wallet } from 'lucide-react';

import { errorMessage } from '@/api/client';
import { cashApi } from '@/api/resources';
import {
  EmptyState,
  Loading,
  Money,
  MoneyInput,
  PageHeader,
  StatCard,
  SubmitButton,
} from '@/components/common';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatGs } from '@/lib/currency';
import { formatDate, formatTime, todayApi } from '@/lib/dates';

/** Sólo las categorías que el usuario puede elegir: 'venta' y 'gasto' las pone el sistema. */
const MANUAL_CATEGORIES = [
  { value: 'aporte', label: 'Aporte de capital', type: 'entrada' },
  { value: 'retiro', label: 'Retiro de dinero', type: 'salida' },
  { value: 'ajuste', label: 'Ajuste de arqueo', type: null },
  { value: 'otro', label: 'Otro', type: null },
];

const CATEGORY_LABELS = {
  venta: 'Venta',
  gasto: 'Gasto',
  aporte: 'Aporte',
  retiro: 'Retiro',
  ajuste: 'Ajuste',
  otro: 'Otro',
};

const emptyForm = () => ({
  type: 'entrada',
  category: 'aporte',
  description: '',
  amount: 0,
  date: todayApi(),
});

export default function Caja() {
  const [date, setDate] = useState(todayApi());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await cashApi.daily(date));
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo cargar la caja'));
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setForm({ ...emptyForm(), date });
    setDialogOpen(true);
  };

  const handleCategoryChange = (category) => {
    const preset = MANUAL_CATEGORIES.find((c) => c.value === category);
    setForm((current) => ({
      ...current,
      category,
      type: preset?.type || current.type,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.description.trim()) {
      toast.error('Poné una descripción del movimiento');
      return;
    }
    if (form.amount <= 0) {
      toast.error('El monto tiene que ser mayor a cero');
      return;
    }

    setSaving(true);
    try {
      await cashApi.create({ ...form, description: form.description.trim() });
      toast.success('Movimiento registrado');
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo registrar el movimiento'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (movement) => {
    if (!window.confirm(`¿Eliminar "${movement.description}"?`)) return;
    try {
      await cashApi.remove(movement.id);
      toast.success('Movimiento eliminado');
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo eliminar el movimiento'));
    }
  };

  const shiftDay = (days) => {
    const d = new Date(`${date}T12:00:00`);
    d.setDate(d.getDate() + days);
    setDate(d.toISOString().slice(0, 10));
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Control de caja" subtitle="Libro diario de entradas y salidas">
        <Button onClick={openNew} className="rounded-sm">
          <Plus size={18} className="mr-2" />
          Nuevo movimiento
        </Button>
      </PageHeader>

      {/* Selector de día */}
      <div className="flex flex-wrap items-center gap-2 rounded-sm border border-border bg-card p-4">
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => shiftDay(-1)}>
          ← Día anterior
        </Button>
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-auto rounded-sm"
        />
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm"
          onClick={() => shiftDay(1)}
          disabled={date >= todayApi()}
        >
          Día siguiente →
        </Button>
        {date !== todayApi() && (
          <Button variant="ghost" size="sm" onClick={() => setDate(todayApi())}>
            Ir a hoy
          </Button>
        )}
      </div>

      {loading || !report ? (
        <Loading />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard
              label="Saldo inicial"
              value={formatGs(report.opening_balance)}
              hint="Arrastre de días anteriores"
            />
            <StatCard
              label="Entradas"
              value={formatGs(report.total_income)}
              icon={ArrowUpRight}
              tone="positive"
            />
            <StatCard
              label="Salidas"
              value={formatGs(report.total_expense)}
              icon={ArrowDownRight}
              tone="negative"
            />
            <StatCard
              label="Saldo final"
              value={formatGs(report.closing_balance)}
              icon={Wallet}
              tone={report.closing_balance >= 0 ? 'positive' : 'negative'}
              hint={formatDate(report.date)}
            />
          </div>

          {report.movements.length === 0 ? (
            <EmptyState
              icon={Wallet}
              title="Sin movimientos este día"
              description="Las ventas y los gastos aparecen acá automáticamente."
              action={
                <Button onClick={openNew} className="rounded-sm">
                  <Plus size={18} className="mr-2" />
                  Nuevo movimiento
                </Button>
              }
            />
          ) : (
            <div className="divide-y divide-border rounded-sm border border-border bg-card">
              {report.movements.map((movement) => {
                const isIncome = movement.type === 'entrada';
                return (
                  <div key={movement.id} className="flex items-center gap-3 p-3">
                    <div
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                        isIncome
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'
                      }`}
                    >
                      {isIncome ? <ArrowUpRight size={17} /> : <ArrowDownRight size={17} />}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{movement.description}</p>
                      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        {formatTime(movement.created_at)}
                        <span>·</span>
                        {CATEGORY_LABELS[movement.category] || movement.category}
                        {movement.auto_generated && (
                          <>
                            <span>·</span>
                            <Lock size={10} />
                            automático
                          </>
                        )}
                      </p>
                    </div>

                    <Money
                      value={movement.amount}
                      className="shrink-0 font-bold"
                      tone={isIncome ? 'positive' : 'negative'}
                    />

                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      disabled={movement.auto_generated}
                      title={
                        movement.auto_generated
                          ? 'Generado por una venta o un gasto: eliminá el origen'
                          : 'Eliminar'
                      }
                      onClick={() => handleDelete(movement)}
                    >
                      <Trash2
                        size={15}
                        className={movement.auto_generated ? 'text-muted-foreground' : 'text-red-500'}
                      />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo movimiento de caja</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="rounded-sm bg-muted p-2 text-xs text-muted-foreground">
              Las ventas y los gastos se registran solos. Usá esto para aportes de capital,
              retiros o ajustes de arqueo.
            </p>

            <div className="space-y-2">
              <Label>Concepto</Label>
              <Select value={form.category} onValueChange={handleCategoryChange}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MANUAL_CATEGORIES.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <div className="grid grid-cols-2 gap-2">
                {['entrada', 'salida'].map((type) => (
                  <Button
                    key={type}
                    type="button"
                    variant={form.type === type ? 'default' : 'outline'}
                    className="rounded-sm capitalize"
                    onClick={() => setForm({ ...form, type })}
                  >
                    {type === 'entrada' ? (
                      <ArrowUpRight size={16} className="mr-2" />
                    ) : (
                      <ArrowDownRight size={16} className="mr-2" />
                    )}
                    {type}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="c-desc">Descripción *</Label>
              <Input
                id="c-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Ej: Aporte del socio"
                required
                className="rounded-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="c-amount">Monto (Gs.) *</Label>
                <MoneyInput
                  id="c-amount"
                  value={form.amount}
                  onChange={(amount) => setForm({ ...form, amount })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-date">Fecha *</Label>
                <Input
                  id="c-date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                  className="rounded-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-sm"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <SubmitButton loading={saving}>Registrar</SubmitButton>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
